import {
  DndContext,
  DndContextProps,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useTheme } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useToast } from '../../hooks/useToast';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from '../context-menu/context-menu';
import { cn } from '../utils';
import {
  onCommitIfEditing,
  useSheetKeyboardAndMouseEffects,
} from './keyboard-and-mouse';
import { MetricsSlot } from './slots';
import { Cell, MergedRegion, Selection, Slot } from './types';
import { cellBoundaries, cellsEqual, cellsInArea } from './utils';

type SheetProps<RawData> = {
  slots: Slot<RawData>[];
  rowCount: number;
  columnCount: number;
  gridSubRows?: number;
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  className?: string;
  onUndo: () => void;
  onRedo: () => void;
  onCopy: (data: readonly RawData[]) => void;
  onPaste: (
    // Will probably become a ClipboardEvent if we ever support the Clipboard API
    event: undefined,
    target: readonly RawData[],
  ) => void;
  onBulkClear: (data: readonly RawData[]) => void;
  getContextMenuContent?: (data: readonly RawData[]) => React.ReactNode;
  disableKeyboardAndMouse?: boolean;
  readOnly?: boolean;
};

/**
 * SC-5486: the sheet hosts both spreadsheet selection and dnd drag on the
 * same pointer gestures. A distance-constrained PointerSensor keeps plain
 * clicks (and click-drag cell selection) from activating a drag, while the
 * whole draggable label can start one once the pointer moves — the same
 * constraint BlockAllView and the workout template editor use. Opt-in: the
 * block builder sheets pass this; other Sheet consumers keep dnd-kit's
 * default sensors.
 */
export function useSheetDndSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );
}

export function Sheet<RawData = undefined>({
  slots,
  rowCount,
  columnCount,
  className,
  gridSubRows = 1,
  gridTemplateColumns,
  gridTemplateRows,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onBulkClear,
  getContextMenuContent,
  disableKeyboardAndMouse = false,
  readOnly,
  ...dndContextProps
}: SheetProps<RawData> & DndContextProps) {
  return (
    <DndContext
      {...dndContextProps}
      onDragEnd={readOnly ? undefined : dndContextProps.onDragEnd}
    >
      <Sheet_Internal
        slots={slots}
        rowCount={rowCount}
        columnCount={columnCount}
        className={className}
        gridSubRows={gridSubRows}
        gridTemplateColumns={gridTemplateColumns}
        gridTemplateRows={gridTemplateRows}
        onUndo={onUndo}
        onRedo={onRedo}
        onCopy={onCopy}
        onPaste={onPaste}
        onBulkClear={onBulkClear}
        getContextMenuContent={readOnly ? undefined : getContextMenuContent}
        disableKeyboardAndMouse={disableKeyboardAndMouse}
        readOnly={readOnly}
      />
    </DndContext>
  );
}

// TODO: useCallback / useMemo where necessary
function Sheet_Internal<RawData = undefined>({
  slots,
  rowCount,
  columnCount,
  className,
  gridTemplateColumns,
  gridTemplateRows,
  onUndo,
  onRedo,
  onCopy,
  onPaste,
  onBulkClear,
  getContextMenuContent,
  gridSubRows = 1,
  disableKeyboardAndMouse = false,
  readOnly,
}: SheetProps<RawData>) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const theme = useTheme();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // True while the contextmenu event being processed came from a cell that
  // allows the sheet menu (SC-5564 review). Right-clicks on unused grid space
  // must not open the menu: no cell mousedown fired, so the menu would act on
  // stale selection data.
  const cellRightClickRef = useRef(false);
  const handleCellRightClick = useCallback((allowed: boolean) => {
    cellRightClickRef.current = allowed;
  }, []);
  const handleGridContextMenu = useCallback((e: React.MouseEvent) => {
    const fromCell = cellRightClickRef.current;
    cellRightClickRef.current = false;
    if (!fromCell) {
      // As of @radix-ui/react-context-menu@2.1.4, the trigger's composed
      // handler skips its own open logic for events that arrive already
      // default-prevented, so this shows the native menu — the same thing the
      // old per-cell disabled triggers did. Re-verify if the Slot handler
      // composition changes in a Radix upgrade (SC-5564 review).
      e.preventDefault();
    }
  }, []);
  // Radix also opens the menu from a 700ms touch/pen long-press started in
  // the trigger's pointerdown handler, which bypasses the contextmenu gate
  // above (SC-5564 review). Track cell pointerdowns the same way and
  // default-prevent touch/pen pointerdowns that did not come from a cell so
  // the long-press timer never starts on unused grid space.
  const cellPointerDownRef = useRef(false);
  const handleCellPointerDown = useCallback((allowed: boolean) => {
    cellPointerDownRef.current = allowed;
  }, []);
  const handleGridPointerDown = useCallback((e: React.PointerEvent) => {
    const fromCell = cellPointerDownRef.current;
    cellPointerDownRef.current = false;
    if (!fromCell && e.pointerType !== 'mouse') {
      e.preventDefault();
    }
  }, []);

  const mergedRegions = useMemo(
    () =>
      slots.flatMap((slot) =>
        slot.region.isMerged
          ? slot.region
          : (slot.region.mergedSubRegions ?? []),
      ),
    [slots],
  );

  const cellLookup = useMemo(() => buildCellLookup(slots), [slots]);

  const getSlotForCell = useCallback(
    (cell: Cell): Slot<RawData> | undefined => {
      return cellLookup.get(`${cell.x}-${cell.y}`)?.slot;
    },
    [cellLookup],
  );

  useClickOutside(
    ref,
    useCallback(() => {
      onCommitIfEditing(selection, getSlotForCell);
      setSelection(null);
    }, [selection, getSlotForCell, setSelection]),
  );

  const selectedRawData = useMemo((): readonly RawData[] => {
    if (!selection) {
      return [];
    }

    const selectionCells = selection.area
      ? cellsInArea(selection.area)
      : [selection.base];

    const selectedSlotCells = new Map<Slot<RawData>, Cell[]>();

    for (const cell of selectionCells) {
      const slot = getSlotForCell(cell);
      if (!slot || !slot.getRawData) {
        continue;
      }
      const entry = selectedSlotCells.get(slot);
      if (!entry) {
        selectedSlotCells.set(slot, [cell]);
      } else {
        entry.push(cell);
      }
    }

    return [...selectedSlotCells.entries()].flatMap(([slot, cells]) => {
      const rawData = slot.getRawData?.(cells) ?? [];
      return rawData.map((data) => ({
        ...data,
        disabled: slot.disabled,
      }));
    });
  }, [selection, getSlotForCell]);

  const contextMenuContent = useMemo(
    () => getContextMenuContent?.(selectedRawData),
    [selectedRawData, getContextMenuContent],
  );

  const handleBulkClear = useCallback(
    () => onBulkClear(selectedRawData),
    [selectedRawData, onBulkClear],
  );

  const handleCopy = useCallback(() => {
    try {
      onCopy(selectedRawData);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        toast({ title: t('clipboardSizeLimitExceeded'), severity: 'error' });
      } else {
        throw e;
      }
    }
  }, [selectedRawData, onCopy, toast, t]);

  const handlePaste = useCallback(
    () => onPaste(undefined, selectedRawData),
    [selectedRawData, onPaste],
  );

  const { onMouseMove, onMouseOver, onMouseDown } =
    useSheetKeyboardAndMouseEffects(
      mergedRegions,
      columnCount,
      rowCount,
      selection,
      setSelection,
      getSlotForCell,
      contextMenuOpen,
      disableKeyboardAndMouse,
      onUndo,
      onRedo,
      handleBulkClear,
      handleCopy,
      handlePaste,
      ref,
    );

  const handleDoubleClick = useCallback(
    (cell: Cell) => {
      if (disableKeyboardAndMouse) return;
      const slot = getSlotForCell(cell);
      const editBehavior = slot?.getEditBehavior(cell);

      if (editBehavior?.type === 'input') {
        editBehavior.onStart();
        setSelection({
          base: cell,
          area: null,
          isHighlighting: false,
          editing: true,
        });
      }
    },
    [disableKeyboardAndMouse, getSlotForCell, setSelection],
  );

  const ROW_HEIGHT = 30;
  const VIRTUALIZE_THRESHOLD = 200;
  const shouldVirtualize = rowCount > VIRTUALIZE_THRESHOLD;

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? rowCount : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 50,
    enabled: shouldVirtualize,
  });

  const renderRowCells = (rowIndex: number) => (
    <Fragment key={rowIndex}>
      {Array.from({ length: columnCount }).map((_, columnIndex) => {
        const cell = {
          x: columnIndex,
          y: rowIndex,
        };

        const selectionProps = getSelectionProps(selection, cell);
        const findResult = cellLookup.get(`${cell.x}-${cell.y}`);

        const editing =
          (selectionProps.selected && selection?.editing) ?? false;

        let height: number;
        let width: number;
        if (findResult?.mergedRegion) {
          if (cellsEqual(cell, findResult.mergedRegion.anchor)) {
            height = findResult.mergedRegion.height;
            width = findResult.mergedRegion.width;
          } else {
            return null;
          }
        } else {
          height = 1;
          width = 1;
        }

        const onBlur = (skipCommit = false) => {
          if (!skipCommit) {
            onCommitIfEditing(selection, getSlotForCell);
          }
          if (selection) {
            setSelection({
              ...selection,
              editing: false,
            });
          }
        };

        const slot = findResult?.slot;
        const renderResult = slot?.renderContent({
          cell,
          editing,
          onBlur,
          refocusSheet: (x, y) => {
            setSelection({
              base: { x, y },
              area: null,
              isHighlighting: false,
              editing: false,
            });
          },
          readOnly,
        });

        const isDisabled =
          slot instanceof MetricsSlot
            ? slot.isDisabledCell(cell)
            : (slot?.disabled ?? false);

        const disableContextMenu = slot?.disableContextMenu ?? false;

        return (
          <CellWrapper
            disabled={isDisabled}
            disableContextMenu={disableContextMenu}
            key={`${columnIndex}-${rowIndex}`}
            {...selectionProps}
            onDoubleClick={() => handleDoubleClick(cell)}
            onMouseDown={(e) => onMouseDown(e, cell)}
            onMouseOver={(e) => onMouseOver(e, cell)}
            cell={cell}
            height={height}
            width={width}
            gridSubRows={gridSubRows}
            overrideGridStyles={renderResult?.overrideGridStyles}
            style={renderResult?.style}
            className={renderResult?.className}
            selectedBorderStyle={renderResult?.selectedBorderStyle}
            backgroundClassName={renderResult?.backgroundClassName}
            onCellRightClick={handleCellRightClick}
            onCellPointerDown={handleCellPointerDown}
          >
            {renderResult?.content}
          </CellWrapper>
        );
      })}
    </Fragment>
  );

  const gridColumnStyle =
    gridTemplateColumns ??
    `repeat(${columnCount * gridSubRows}, minmax(${75 / gridSubRows}px, 1fr))`;

  const gridRowStyle =
    gridTemplateRows ?? `repeat(${rowCount}, ${ROW_HEIGHT}px)`;

  const grid = shouldVirtualize ? (
    <div
      ref={(node) => {
        (
          scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = node;
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      tabIndex={0}
      className={cn(
        'min-h-0 select-none overscroll-none overflow-auto',
        className,
      )}
      onContextMenu={handleGridContextMenu}
      onPointerDown={handleGridPointerDown}
      onMouseMove={onMouseMove}
    >
      <div
        className="grid relative"
        style={{
          gridTemplateColumns: gridColumnStyle,
          gridTemplateRows: gridRowStyle,
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer
          .getVirtualItems()
          .map((virtualRow) => renderRowCells(virtualRow.index))}
      </div>
    </div>
  ) : (
    <div
      ref={ref}
      tabIndex={0}
      className={cn(
        'grid h-full min-h-0 overflow-auto overscroll-none select-none',
        className,
      )}
      style={{
        gridTemplateColumns: gridColumnStyle,
        gridTemplateRows: gridRowStyle,
      }}
      onContextMenu={handleGridContextMenu}
      onPointerDown={handleGridPointerDown}
      onMouseMove={onMouseMove}
    >
      {Array.from({ length: rowCount }).map((_, i) => renderRowCells(i))}
    </div>
  );

  if (!contextMenuContent) {
    return grid;
  }

  // One menu for the whole sheet (SC-5564) instead of one Radix provider stack
  // per cell, so full-sheet re-renders walk plain divs only. Radix opens the
  // menu at the right-clicked pointer position; cells that disable their
  // context menu stop the event in CellWrapper, so the native menu shows there
  // exactly as the old per-cell disabled trigger did.
  return (
    <ContextMenu
      // Important so that useClickOutside is not triggered when closing
      modal={false}
      onOpenChange={(open) => {
        if (open) {
          setContextMenuOpen(true);
        }
      }}
    >
      <ContextMenuTrigger asChild>{grid}</ContextMenuTrigger>
      <ContextMenuContent
        // Overrides animations, see styles.css. Might want to move this into the component library?
        className="ContextMenuContent"
        // Needed for context menus inside of drawers to work properly
        style={{ zIndex: theme.zIndex.drawer + 1 }}
        // Prevents useClickOutside from being triggered when choosing an option
        onMouseDown={(e) => e.stopPropagation()}
        onInteractOutside={() => setContextMenuOpen(false)}
        onEscapeKeyDown={() => setContextMenuOpen(false)}
        onCloseAutoFocus={() => setContextMenuOpen(false)}
      >
        {contextMenuContent}
      </ContextMenuContent>
    </ContextMenu>
  );
}

interface CellWrapperProps {
  disabled: boolean;
  disableContextMenu: boolean;
  selected: boolean;
  inSelection: boolean;
  gridSubRows: number;
  onCellRightClick: (allowed: boolean) => void;
  onCellPointerDown: (allowed: boolean) => void;
  cell: Cell;
  height: number;
  width: number;
  children: React.ReactNode;
  onMouseOver: (e: React.MouseEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  style: React.CSSProperties | undefined;
  selectedBorderStyle: string | undefined;
  className: string | undefined;
  overrideGridStyles: boolean | undefined;
  backgroundClassName: string | undefined;
}

export const CELL_CLASSNAME_UNSELECTED =
  'border-[.5px] border-gray-300 hover:border-gray-400';
export const CELL_CLASSNAME_SELECTED = 'border-2 border-blue-500';

function CellWrapper({
  disabled,
  disableContextMenu,
  selected,
  inSelection,
  children,
  cell,
  gridSubRows,
  height,
  width,
  onCellRightClick,
  onCellPointerDown,
  onDoubleClick,
  onMouseDown,
  onMouseOver,
  style,
  className,
  overrideGridStyles = false,
  selectedBorderStyle,
  backgroundClassName,
}: CellWrapperProps) {
  const ref = useRef<HTMLDivElement>(null);
  useScrollTo(ref, selected);

  return (
    <div
      ref={ref}
      onMouseOver={(e) => onMouseOver(e)}
      onMouseDown={(e) => onMouseDown(e)}
      onPointerDown={() => {
        // Flag the origin for the sheet-level long-press gate (SC-5564
        // review): menu-capable cells allow it, menu-disabled cells do not.
        onCellPointerDown(!disableContextMenu);
      }}
      onDoubleClick={onDoubleClick}
      onContextMenu={(e) => {
        // Preserve the old per-cell trigger's disabled behavior (SC-5564):
        // right-clicking this cell must not open the sheet-level menu.
        if (disableContextMenu) {
          onCellRightClick(false);
          e.stopPropagation();
        } else {
          // Tell the sheet-level trigger this right-click came from a cell
          // that allows the menu.
          onCellRightClick(true);
        }
      }}
      className={cn(
        'cursor-default',
        'flex items-center text-sm',
        selected
          ? (selectedBorderStyle ?? CELL_CLASSNAME_SELECTED)
          : CELL_CLASSNAME_UNSELECTED,
        inSelection ? 'bg-blue-50' : backgroundClassName,
        disabled && 'opacity-50',
        className,
      )}
      style={{
        ...(!overrideGridStyles && {
          gridColumnStart: cell.x * gridSubRows + 1,
          gridRowStart: cell.y + 1,
          gridColumnEnd: `span ${width * gridSubRows}`,
          gridRowEnd: `span ${height}`,
        }),
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function getSelectionProps(
  selection: Selection | null,
  cell: Cell,
): {
  selected: boolean;
  inSelection: boolean;
} {
  if (!selection) {
    return {
      selected: false,
      inSelection: false,
    };
  }
  const selected = cellsEqual(selection.base, cell);
  if (!selection.area) {
    return {
      selected,
      inSelection: false,
    };
  }

  const { loX, loY, hiX, hiY } = cellBoundaries(
    selection.area.from,
    selection.area.to,
  );
  const inSelection =
    cell.x >= loX && cell.x <= hiX && cell.y >= loY && cell.y <= hiY;
  return {
    selected,
    inSelection,
  };
}

function useScrollTo(ref: React.RefObject<HTMLDivElement>, selected: boolean) {
  const hasScrolledTo = useRef(false);

  useEffect(() => {
    if (!selected) {
      hasScrolledTo.current = false;
      return;
    }
    const target = ref.current;
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        if (entry.intersectionRatio !== 1 && !hasScrolledTo.current) {
          hasScrolledTo.current = true;
          target.scrollIntoView({
            behavior: 'instant',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      },
      {
        rootMargin: '0px 0px 100%',
      },
    );

    observer.observe(target);
    return () => {
      observer.unobserve(target);
    };
  }, [ref, selected]);
}

// SC-5486: rendering the grid resolves every cell against the slots once
// per render. The previous implementation scanned all slots (and their
// sub-regions) per cell — O(cells × slots) on the sheet's hot path. Flatten
// the regions into a cell-keyed map once per slots change instead.
// Exported for tests.
export function buildCellLookup<RawData>(
  slots: Slot<RawData>[],
): Map<string, { slot: Slot<RawData>; mergedRegion?: MergedRegion }> {
  const lookup = new Map<
    string,
    { slot: Slot<RawData>; mergedRegion?: MergedRegion }
  >();
  const addRegionCells = (
    slot: Slot<RawData>,
    anchor: Cell,
    width: number,
    height: number,
    mergedRegion?: MergedRegion,
  ) => {
    for (let y = anchor.y; y < anchor.y + height; y++) {
      for (let x = anchor.x; x < anchor.x + width; x++) {
        const key = `${x}-${y}`;
        const existing = lookup.get(key);
        // First slot wins across overlapping slots (matches the previous
        // slots.find scan, SC-5564 review). A merged subregion still replaces
        // its own slot's plain-region entry.
        if (existing && existing.slot !== slot) {
          continue;
        }
        lookup.set(key, { slot, mergedRegion });
      }
    }
  };
  for (const slot of slots) {
    if (slot.region.isMerged) {
      addRegionCells(
        slot,
        slot.region.anchor,
        slot.region.width,
        slot.region.height,
        slot.region,
      );
    } else {
      // Cells inside the region but outside every sub-region still resolve
      // to the slot, as plain unmerged cells (matches the previous
      // slots.find scan: no mergedRegion).
      addRegionCells(
        slot,
        slot.region.anchor,
        slot.region.width,
        slot.region.height,
      );
      for (const subRegion of slot.region.mergedSubRegions ?? []) {
        addRegionCells(
          slot,
          subRegion.anchor,
          subRegion.width,
          subRegion.height,
          subRegion,
        );
      }
    }
  }
  return lookup;
}
